import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Fragment, useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import FormHeader from "@/components/FormHeader"
import Modal from "@/components/Modal"
import SkeletonRow from "@/components/SkeletonRow"
import { Badge } from "@/components/ui/badge"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import useApiBaseUrl from "@/hooks/useApiBaseUrl"
import { useDebounce } from "@/hooks/useDebounce"
import api from "@/lib/axios"
import { formatDateIndonesia, formatRupiah } from "@/lib/utils"
import { getListPeriode, getListUnitkerja } from "@/services/getData"
import { DataPendaftaran, PaginationData, Periode, Unitkerja } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import toast from "react-hot-toast"
import { LuListFilter } from "react-icons/lu"
import { MdSync } from "react-icons/md"
import { RiFileExcel2Fill } from "react-icons/ri"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom"
import FormFilter from "./FormFilter"
import FormUpdate from "./FormUpdate"
import { Pagination } from "./Pagination"
import { FaUserGraduate } from "react-icons/fa6"
import { useTahun } from "@/hooks/useTahun"

interface DataTableProps<TData extends DataPendaftaran, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  paginationData: PaginationData<DataPendaftaran[]>
  isPending: boolean
}

const postDataPenetapanUkt = async ({
  id_sys_periode,
}: {
  id_sys_periode: string
}) => {
  const response = await api.post(
    "/operator-internasional/data-pendaftaran/sync/penetapan-ukt",
    {
      id_sys_periode,
    }
  )
  return response.data
}

export function DataTable<TData extends DataPendaftaran, TValue>({
  columns,
  data,
  paginationData,
  isPending: isPendingDataPendaftaran,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})

  const [searchParams] = useSearchParams()
  const ids_ref_jalur_jenis = searchParams.get("ids_ref_jalur_jenis")
  const ids_uk = searchParams.get("ids_uk")

  const [form, setForm] = useState({
    id_sys_periode: "",
    tahun: `${new Date().getFullYear()}` || "",
  })

  const [selected, setSelected] = useState<DataPendaftaran | null>(null)
  const [showUpdate, setShowUpdate] = useState(false)
  const [showSyncUkt, setShowSyncUkt] = useState(false)
  const [showFilter, setShowFilter] = useState(false)
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || ""
  )
  const debouncedQuery = useDebounce(searchQuery, 1000)
  const navigate = useNavigate()
  const location = useLocation()
  const { apiBaseUrl } = useApiBaseUrl()
  const { selectedTahun } = useTahun()

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  const { data: listUnitkerja } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
  })
  const listProdi = listUnitkerja?.filter((unitkerja: Unitkerja) => {
    return unitkerja.unitkerja_group_key == "5" && unitkerja.is_active
  })
  const ids_uk_array = ids_uk?.split("-")
  const listProdiAktifFilter =
    listProdi?.filter((item: Unitkerja) => {
      return ids_uk_array?.includes(String(item.uk_id))
    }) || []

  const { data: dataPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(),
  })
  const listPeriodeInternasional = dataPeriode
    ? dataPeriode?.filter((periode: Periode) => {
        return periode.jalur_jenis?.nama_jalur_jenis_slug === "internasional"
      })
    : []

  const queryClient = useQueryClient()

  const { mutate: mutateSyncUkt, isPending: isPendingSyncUkt } = useMutation({
    mutationFn: postDataPenetapanUkt,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataPendaftaran"] })
      setShowSyncUkt(false)
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSyncDataPenetapanUkt = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()
    mutateSyncUkt({
      id_sys_periode: form.id_sys_periode,
    })
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)

    if (debouncedQuery) {
      // if (params.get("search") !== debouncedQuery) {
      //   params.delete("page");
      // }

      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery)
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        })
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search")
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        })
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search])

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="flex-1 flex items-center gap-3">
          <Input
            autoFocus
            placeholder="Filter by tahun, nomor pendaftaran, nama lengkap, status kelulusan, dan pilihan prodi..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="max-w-sm"
          />
          <Button
            variant={ids_ref_jalur_jenis ? "default" : "outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowFilter(true)}
          >
            Filter
            <LuListFilter />
          </Button>

          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => {
              const url = new URL(
                `${apiBaseUrl}/operator-internasional/data-pendaftaran/export`
              )

              const params = new URLSearchParams(window.location.search)

              if (selectedTahun) params.set("tahun", selectedTahun?.label)

              url.search = params.toString()

              window.open(url.toString(), "_blank")
            }}
          >
            Export
            <RiFileExcel2Fill />
          </Button>

          <div className="flex gap-1 flex-wrap">
            {listProdiAktifFilter?.map((item: Unitkerja) => (
              <Badge variant={"outline"}>{item?.nama}</Badge>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSyncUkt(true)}
          >
            Sinkronisasi Penetapan UKT
            <MdSync />
          </Button>

          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowUpdate(true)}
          >
            Update Siswa Lulus
            <FaUserGraduate />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {isPendingDataPendaftaran ? (
        <div className="flex flex-col gap-y-3">
          {Array(10)
            .fill(null)
            .map((_, index) => {
              return <SkeletonRow key={index} />
            })}
        </div>
      ) : (
        <div className="border rounded-xl">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      return (
                        <Fragment key={header.id}>
                          <TableHead>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                          {index === 0 && <TableHead key={"0"}></TableHead>}
                        </Fragment>
                      )
                    })}
                    {[...Array(1)].map((_, index) => {
                      return (
                        <Fragment key={index}>
                          <TableHead>Pilihan Prodi</TableHead>
                        </Fragment>
                      )
                    })}
                    <TableHead className="whitespace-nowrap">
                      Konsentrasi Prodi
                    </TableHead>
                  </TableRow>
                )
              })}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <>
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>

                        {index === 0 && (
                          <TableCell>
                            <Drawer>
                              <DrawerTrigger
                                onClick={() => setSelected(row.original)}
                              >
                                Lihat
                              </DrawerTrigger>
                              <DrawerContent className="h-[90vh]">
                                <DrawerHeader>
                                  <DrawerTitle>Data Pendaftaran</DrawerTitle>
                                  <DrawerDescription>
                                    {selected?.data_siswa?.nama_lengkap}
                                  </DrawerDescription>
                                </DrawerHeader>

                                <section className="p-5 grid lg:grid-cols-2 grid-cols-1 gap-5">
                                  <div className="flex-1 overflow-auto rounded-xl border">
                                    <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
                                      <tbody>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Nama
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {selected?.data_siswa?.nama_lengkap}
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Nomor Pendaftaran
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {selected?.nomor_pendaftaran}
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Email
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {selected?.data_siswa?.email}
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Jenis Kelamin
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {selected?.data_siswa
                                              ?.jenis_kelamin == "L"
                                              ? "Laki-Laki"
                                              : selected?.data_siswa
                                                  ?.jenis_kelamin == "P"
                                              ? "Perempuan"
                                              : "-"}
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Tanggal Lahir
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {selected?.data_siswa?.tanggal_lahir
                                              ? formatDateIndonesia(
                                                  selected?.data_siswa
                                                    ?.tanggal_lahir,
                                                  "medium"
                                                )
                                              : "-"}
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Jalur
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {
                                              selected?.periode?.jalur_jenis
                                                ?.nama_jalur_jenis
                                            }
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Tahun Pendaftaran
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {selected?.periode?.tahun}
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Pilihan Program Studi
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {selected?.data_pilihan_prodi &&
                                              selected?.data_pilihan_prodi[0]
                                                ?.unitkerja?.nama}
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Pilihan Konsentrasi Program Studi
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {(selected?.data_pilihan_prodi &&
                                              selected?.data_pilihan_prodi[0]
                                                .konsentrasi_prodi
                                                ?.nama_konsentrasi) ??
                                              "-"}
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Status Validasi Pendaftaran
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {!row.original
                                              .is_valid_pendaftaran ||
                                            row.original.is_valid_pendaftaran ==
                                              0 ? (
                                              <Badge variant={"secondary"}>
                                                Belum Divalidasi Peserta
                                              </Badge>
                                            ) : (
                                              <Badge>
                                                Sudah Divalidasi Peserta
                                              </Badge>
                                            )}
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Status Validasi Pendaftaran
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {!row.original.is_lulus ||
                                            row.original.is_lulus == 0 ? (
                                              <Badge variant={"secondary"}>
                                                Belum Lulus
                                              </Badge>
                                            ) : (
                                              <Badge>Lulus</Badge>
                                            )}
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Status Daftarkembali
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {!row.original.is_daftar_kembali ||
                                            row.original.is_daftar_kembali ==
                                              0 ? (
                                              <Badge variant={"secondary"}>
                                                Belum Daftarkembali
                                              </Badge>
                                            ) : (
                                              <Badge>Sudah Daftarkembali</Badge>
                                            )}
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            Nominal UKT
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {row.original.data_penetapan_ukt
                                              ?.nominal_ukt
                                              ? formatRupiah(
                                                  Number(
                                                    row.original
                                                      .data_penetapan_ukt
                                                      ?.nominal_ukt
                                                  )
                                                )
                                              : "-"}
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className="py-1 px-3 border font-semibold text-start">
                                            NIM
                                          </th>
                                          <td className="py-1 px-3 border text-center">
                                            :
                                          </td>
                                          <td className="py-1 px-3 border">
                                            {row.original.nim ?? "-"}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>

                                  <div className="flex-1 overflow-auto h-fit rounded-xl border">
                                    <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
                                      <thead>
                                        <tr>
                                          <th className="px-2 py-1 font-semibold border text-start">
                                            Jenis Berkas
                                          </th>
                                          <th className="px-2 py-1 font-semibold border text-start">
                                            Keterangan
                                          </th>
                                          <th className="px-2 py-1 font-semibold border">
                                            Berkas
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {row.original.data_berkas?.length >
                                        0 ? (
                                          row.original.data_berkas?.map(
                                            (dataBerkas) => {
                                              return (
                                                <tr>
                                                  <td className="px-2 py-1 border">
                                                    {
                                                      dataBerkas?.sys_berkas
                                                        ?.jenis_berkas
                                                        ?.nama_jenis_berkas
                                                    }
                                                  </td>
                                                  <td className="px-2 py-1 border">
                                                    {
                                                      dataBerkas?.sys_berkas
                                                        ?.jenis_berkas
                                                        ?.keterangan
                                                    }
                                                  </td>
                                                  <td className="px-2 py-1 border">
                                                    <div className="flex justify-center">
                                                      <Link
                                                        to={
                                                          dataBerkas?.path_berkas ||
                                                          ""
                                                        }
                                                        target="_blank"
                                                      >
                                                        <Button
                                                          variant={"outline"}
                                                        >
                                                          Lihat Berkas
                                                        </Button>
                                                      </Link>
                                                    </div>
                                                  </td>
                                                </tr>
                                              )
                                            }
                                          )
                                        ) : (
                                          <tr>
                                            <td
                                              className="px-2 py-1 border"
                                              colSpan={3}
                                            >
                                              Data berkas tidak ditemukan.
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </section>
                                <DrawerFooter>
                                  {/* <Button>Submit</Button> */}
                                  <DrawerClose className="w-full">
                                    <Button
                                      variant="default"
                                      className="w-full"
                                    >
                                      Tutup
                                    </Button>
                                  </DrawerClose>
                                </DrawerFooter>
                              </DrawerContent>
                            </Drawer>
                          </TableCell>
                        )}
                      </>
                    ))}
                    {[...Array(1)].map((_, index) => {
                      const pilihanProdi =
                        row?.original.data_pilihan_prodi?.find(
                          (prodi) => prodi.urutan_program_studi === index + 1
                        )

                      return (
                        <Fragment key={index}>
                          <TableCell>
                            {pilihanProdi ? (
                              <Badge
                                variant={
                                  pilihanProdi?.is_lulus ? "default" : "dark"
                                }
                                className="whitespace-nowrap"
                              >
                                {pilihanProdi?.unitkerja?.nama}
                              </Badge>
                            ) : (
                              <Badge variant={"outline"}>Kosong</Badge>
                            )}
                          </TableCell>
                        </Fragment>
                      )
                    })}
                    <TableCell>
                      {row.original.data_pilihan_prodi &&
                        row.original.data_pilihan_prodi[0]
                          ?.konsentrasi_prodi && (
                          <Badge className="whitespace-nowrap">
                            {
                              row.original.data_pilihan_prodi[0]
                                .konsentrasi_prodi?.nama_konsentrasi
                            }
                          </Badge>
                        )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 3}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <br />
      <div>
        <Pagination table={table} paginationData={paginationData} />
      </div>

      <Modal
        show={showSyncUkt}
        onClose={() => setShowSyncUkt(false)}
        closeable={!isPendingSyncUkt}
      >
        <form
          onSubmit={handleSyncDataPenetapanUkt}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data Hasil Penetapan UKT"
            description={`Apakah anda yakin akan melakukan sinkronisasi data?`}
          />
          <div>
            <Label>Periode</Label>
            <Select
              required
              value={form.id_sys_periode}
              onValueChange={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }))
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={"Pilih Periode"} />
              </SelectTrigger>
              <SelectContent>
                {listPeriodeInternasional?.map((periode: Periode) => (
                  <SelectItem
                    key={periode.id_sys_periode}
                    value={`${periode.id_sys_periode}`}
                  >
                    {periode.tahun} - {periode.jalur_jenis?.nama_jalur_jenis} -{" "}
                    Gelombang {periode.gelombang}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingSyncUkt}
              onClick={() => setShowSyncUkt(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingSyncUkt}
            >
              {isPendingSyncUkt ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal show={showFilter} onClose={() => setShowFilter(false)} width="2xl">
        <FormFilter closeModal={() => setShowFilter(false)} />
      </Modal>

      <Modal
        show={showUpdate}
        onClose={() => setShowUpdate(false)}
        closeable={false}
        className="md:w-[50vw]"
      >
        <FormUpdate
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          listDataPendaftaran={paginationData}
          isPendingListDataPendaftaran={isPendingDataPendaftaran}
          closeModal={() => setShowUpdate(false)}
        />
      </Modal>
    </div>
  )
}
